import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Code2, Bot, TrendingUp, CreditCard, Palette,
  Film, CalendarDays, Users2, Sparkles, Layers3, Cpu
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';

const reveal = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] } }
};

const tech = [
  {
    n: '01', icon: Code2, title: 'BUILD',
    text: 'Web, apps, software a medida, plataformas digitales, marketplaces, CRM y sistemas administrativos.'
  },
  {
    n: '02', icon: Bot, title: 'AUTOMATE',
    text: 'Inteligencia artificial, asistentes conversacionales, automatización, APIs y flujos conectados.'
  },
  {
    n: '03', icon: TrendingUp, title: 'GROW',
    text: 'Marca, contenido, redes sociales, campañas, captación, funnels y automatización comercial.'
  },
  {
    n: '04', icon: CreditCard, title: 'MONETIZE',
    text: 'E-commerce, membresías, reservaciones, suscripciones, pagos y plataformas preparadas para vender.'
  }
];

const art = [
  { icon: Palette, title: 'Brand & Identity', text: 'Creamos marcas con propósito, identidad, narrativa y posicionamiento.' },
  { icon: Film, title: 'Content & Media', text: 'Contenido audiovisual, podcast, video, reels y producción multiplataforma.' },
  { icon: CalendarDays, title: 'Experiences', text: 'Eventos, activaciones y experiencias que conectan marcas con audiencias.' },
  { icon: Users2, title: 'Talent', text: 'Booking, management y plataforma especializada de talento y proveedores.' }
];

const flow = ['IDEA','MARCA','PLATAFORMA','CONTENIDO','AUDIENCIA','LEADS','CONVERSIÓN','PAGO','DATOS','ESCALAMIENTO'];

export default function HomePage() {
  return (
    <Layout>
      <section className="gs-hero min-h-screen flex items-center overflow-hidden relative">
        <div className="gs-grid-overlay" />
        <div className="gs-orbit gs-orbit-a" />
        <div className="gs-orbit gs-orbit-b" />
        <div className="container-premium relative z-10 pt-32 pb-20">
          <div className="grid lg:grid-cols-[1.08fr_.92fr] gap-12 items-center">
            <motion.div initial="hidden" animate="visible" className="max-w-4xl">
              <motion.p variants={reveal} className="section-kicker mb-6">GS AGENCY · ART & TECHNOLOGY</motion.p>
              <motion.h1 variants={reveal} className="gs-display text-5xl sm:text-6xl md:text-7xl xl:text-[5.8rem] leading-[0.94] mb-8">
                TECNOLOGÍA PARA CREAR,<br/>
                <span className="metal-text">IMPULSAR Y MONETIZAR</span><br/>
                MARCAS.
              </motion.h1>
              <motion.p variants={reveal} className="text-white/72 text-lg md:text-xl leading-relaxed max-w-3xl mb-4">
                Creamos ecosistemas digitales que integran tecnología, estrategia, marca, contenido, automatización y ventas.
              </motion.p>
              <motion.p variants={reveal} className="text-white/46 max-w-2xl leading-relaxed mb-10">
                GS Agency transforma ideas, empresas y marcas mediante software, inteligencia artificial, plataformas digitales, contenido y experiencias diseñadas para crecer.
              </motion.p>
              <motion.div variants={reveal} className="flex flex-col sm:flex-row gap-4">
                <Link to="/technology" className="btn-metal inline-flex items-center justify-center gap-2">
                  Explorar Technology <ArrowRight size={17}/>
                </Link>
                <Link to="/creative-art" className="btn-metal-outline inline-flex items-center justify-center gap-2">
                  Descubrir Creative Art
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: .96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="relative flex justify-center lg:justify-end"
            >
              <div className="gs-logo-stage">
                <img
                  src="/brand-assets/gs-agency-logo-silver.png"
                  alt="GS Agency Art & Technology"
                  className="w-[320px] md:w-[430px] lg:w-[520px] max-w-full drop-shadow-[0_0_34px_rgba(255,255,255,0.12)]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="grid lg:grid-cols-[.85fr_1.15fr] gap-14 items-end mb-16">
            <div>
              <p className="section-kicker mb-4">DIVISIÓN PRINCIPAL</p>
              <h2 className="gs-display text-4xl md:text-6xl">SMART <span className="metal-text">TECHNOLOGY</span></h2>
            </div>
            <p className="text-white/55 text-lg leading-relaxed max-w-3xl">
              Desarrollamos tecnología a la medida para convertir ideas, procesos y modelos de negocio en soluciones digitales inteligentes, escalables y rentables.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-px bg-white/10 editorial-frame overflow-hidden">
            {tech.map(({n,icon:Icon,title,text}) => (
              <div key={title} className="gs-panel p-8 md:p-10 min-h-[300px] flex flex-col">
                <div className="flex items-center justify-between mb-12">
                  <span className="text-white/30 tracking-[.28em] text-xs">{n}</span>
                  <Icon size={22} className="text-[#d7dbe0]"/>
                </div>
                <h3 className="font-accent text-xl tracking-[.14em] text-white mb-4">{title}</h3>
                <p className="text-white/50 leading-relaxed text-sm">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-end">
            <Link to="/technology" className="link-metal">Conoce nuestras soluciones tecnológicas <ArrowRight size={16}/></Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#080808] border-y border-white/7 relative overflow-hidden">
        <div className="silver-wave silver-wave-left"/>
        <div className="container-premium relative z-10">
          <div className="max-w-4xl mb-14">
            <p className="section-kicker mb-4">FROM BRAND TO BUSINESS</p>
            <h2 className="gs-display text-4xl md:text-6xl mb-6">
              NO SOLO CREAMOS TU MARCA.<br/>
              <span className="metal-text">CONSTRUIMOS EL SISTEMA PARA HACERLA CRECER.</span>
            </h2>
          </div>
          <div className="gs-flow">
            {flow.map((item, i) => (
              <React.Fragment key={item}>
                <div className="gs-flow-item">{item}</div>
                {i < flow.length - 1 && <div className="gs-flow-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="gs-image-frame">
              <img src="/brand-assets/creative-about.webp" alt="Creative Art" className="gs-photo"/>
              <div className="gs-image-sheen"/>
            </div>
            <div>
              <p className="section-kicker mb-4">SEGUNDA DIVISIÓN</p>
              <h2 className="gs-display text-4xl md:text-6xl mb-7">CREATIVE <span className="metal-text">ART</span></h2>
              <p className="text-white/58 text-lg leading-relaxed mb-10">
                Creamos marcas, contenidos, eventos y experiencias que conectan talento, empresas y audiencias, integrados dentro del mismo ecosistema tecnológico y comercial.
              </p>
              <div className="grid sm:grid-cols-2 gap-5">
                {art.map(({icon:Icon,title,text}) => (
                  <div key={title} className="border-t border-white/14 pt-5">
                    <Icon size={18} className="text-[#d7dbe0] mb-3"/>
                    <h3 className="text-white font-medium mb-2">{title}</h3>
                    <p className="text-white/42 text-sm leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
              <Link to="/creative-art" className="link-metal mt-10 inline-flex">Descubre nuestra división creativa <ArrowRight size={16}/></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#090909] relative overflow-hidden">
        <div className="container-premium">
          <div className="grid lg:grid-cols-[1fr_.9fr] gap-16 items-center">
            <div>
              <p className="section-kicker mb-4">MODELO 360°</p>
              <h2 className="gs-display text-4xl md:text-6xl mb-6">ESTRATEGIA + TECNOLOGÍA + <span className="metal-text">CREATIVIDAD</span></h2>
              <p className="text-white/52 text-lg leading-relaxed mb-8 max-w-2xl">
                Estrategia, identidad, tecnología, contenido, distribución, conversión, experiencia y resultados dentro de una sola agencia.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['ESTRATEGIA','IDENTIDAD','TECNOLOGÍA','CONTENIDO','DISTRIBUCIÓN','CONVERSIÓN','EXPERIENCIA','RESULTADOS'].map((x,i)=>(
                  <div className="model-chip" key={x}><span>{String(i+1).padStart(2,'0')}</span>{x}</div>
                ))}
              </div>
            </div>
            <div className="relative flex justify-center">
              <div className="model-orbit">
                <div className="model-orbit-inner">
                  <Cpu size={44} className="text-[#e8eaed]"/>
                  <span>GS</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="container-premium">
          <div className="editorial-frame p-8 md:p-14 relative overflow-hidden">
            <div className="silver-wave silver-wave-right"/>
            <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-end">
              <div>
                <p className="section-kicker mb-4">¿LISTO PARA EL SIGUIENTE NIVEL?</p>
                <h2 className="gs-display text-4xl md:text-6xl max-w-4xl mb-5">
                  HAGAMOS QUE TU MARCA <span className="metal-text">SE VEA, SE ESCUCHE Y SE MONETICE.</span>
                </h2>
                <p className="text-white/48">Cuéntanos qué quieres construir. Nosotros diseñamos el ecosistema.</p>
              </div>
              <Link to="/contact" className="btn-metal inline-flex items-center gap-2 whitespace-nowrap">
                Cuéntanos tu proyecto <ArrowRight size={17}/>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
